/**
 * Multi-language related operations
 */
import { i18n } from './setupI18n';
import { useLocaleStoreWithOut } from '/@/store/modules/locale';
import { unref, computed } from 'vue';
import { loadLocalePool, setHtmlPageLang } from './helper';
import { LangEnum } from '../enums/appEnum';
import dayjs from 'dayjs';

interface LangModule {
  message: Recordable;
  momentLocale: Recordable;
  momentLocaleName: string;
}

function setI18nLanguage(locale: LangEnum) {
  const localeStore = useLocaleStoreWithOut();

  if (i18n.mode === 'legacy') {
    i18n.global.locale = locale;
  } else {
    (i18n.global.locale as any).value = locale;
  }
  localeStore.setLocaleInfo({ locale });
  setHtmlPageLang(locale);
}

export function useLocale() {
  const localeStore = useLocaleStoreWithOut();
  const getLocale = computed(() => localeStore.getLocale);
  const getShowLocalePicker = computed(() => localeStore.getShowPicker);

  const getAntdLocale = computed((): any => {
    return i18n?.global.getLocaleMessage(unref(getLocale))?.antdLocale ?? {};
  });

  const getDirection = computed(() => {
    if (getLocale.value == LangEnum.AR_AE) {
      return 'rtl';
    } else {
      return 'ltr';
    }
  });

  // Switching the language will change the locale of useI18n
  // And submit to configuration modification
  async function changeLocale(locale: LangEnum) {
    // locale = 'en-US';
    const globalI18n = i18n.global;
    const currentLocale = unref(globalI18n.locale);

    const langModule = ((await import(`./lang/${locale}.ts`)) as any).default as LangModule;
    const { message, momentLocale, momentLocaleName } = langModule;
    globalI18n.setLocaleMessage(locale, message);
    dayjs.locale(momentLocaleName, momentLocale);

    loadLocalePool.push(locale);
    if (currentLocale === locale) {
      return locale;
    }
    if (loadLocalePool.includes(locale)) {
      setI18nLanguage(locale);
      return locale;
    }

    if (!langModule) return;

    setI18nLanguage(locale);
    return locale;
  }

  return {
    getLocale,
    getDirection,
    getShowLocalePicker,
    changeLocale,
    getAntdLocale,
  };
}
